import { OptionsObject, SnackbarMessage, useSnackbar } from 'notistack'
import { useCallback, useContext } from 'react'

import { removeDispensaryPrefixAndSlug } from '../../dispensaries'
import { useLocationContext } from '../../pages/hooks/useLocationContext'
import { ToastContext, ToastId } from '../context'

export interface UsePersistentToastOptions {
  toastId: ToastId
  condition?: () => boolean
  excludedPathPatterns?: RegExp[]
}

export type PersistentToastOptions = Omit<
  OptionsObject,
  'key' | 'persist' | 'preventDuplicate' | 'autoHideDuration'
>

export interface UsePersistentToast {
  toastId: ToastId
  shouldShowToast: boolean
  shouldHideToast: boolean
  isToastShown: boolean
  showToast: (message: SnackbarMessage, options?: PersistentToastOptions) => void
  hideToast: () => void
  dismissToast: () => void
}

const sharedExcludedPathPatterns = [/^\/checkout/]

const useIsExcludedPath = (excludedPathPatterns?: RegExp[]): boolean => {
  const location = useLocationContext()
  const path = removeDispensaryPrefixAndSlug(location?.pathname ?? '/')
  const patterns = [...sharedExcludedPathPatterns, ...(excludedPathPatterns ?? [])]
  return patterns.some((pattern) => pattern.test(path))
}

export const usePersistentToast = ({
  toastId,
  condition,
  excludedPathPatterns,
}: UsePersistentToastOptions): UsePersistentToast => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar()
  const {
    isToastShown: getIsToastShown,
    isToastDismissed: getIsToastDismissed,
    hideToast,
    showToast,
    dismissToast,
  } = useContext(ToastContext)

  const isToastDismissed = getIsToastDismissed(toastId)
  const isToastShown = getIsToastShown(toastId) && !getIsToastDismissed(toastId)

  const isConditionMet = condition?.() ?? true
  const isExcludedPath = useIsExcludedPath(excludedPathPatterns)

  const shouldShowToast = !isToastShown && !isToastDismissed && isConditionMet && !isExcludedPath
  const shouldHideToast = isToastShown && (!isConditionMet || isExcludedPath)

  const showPersistentToast = useCallback<UsePersistentToast['showToast']>(
    (message, options) => {
      enqueueSnackbar(message, {
        ...options,
        key: toastId,
        persist: true,
        autoHideDuration: 10000,
        preventDuplicate: true,
        style: {
          ...(options?.onClick ? { cursor: 'pointer' } : {}),
          ...options?.style,
        },
        onClose: (...args) => {
          options?.onClose?.(...args)
          hideToast(toastId)
        },
      })
      showToast(toastId)
    },
    [enqueueSnackbar, showToast, hideToast, toastId],
  )

  const hidePersistentToast = useCallback<UsePersistentToast['hideToast']>(() => {
    closeSnackbar(toastId)
    hideToast(toastId)
  }, [closeSnackbar, hideToast, toastId])

  const dismissPersistentToast = useCallback<UsePersistentToast['dismissToast']>(() => {
    closeSnackbar(toastId)
    dismissToast(toastId)
  }, [closeSnackbar, dismissToast, toastId])

  return {
    toastId,
    shouldShowToast,
    shouldHideToast,
    isToastShown,
    showToast: showPersistentToast,
    hideToast: hidePersistentToast,
    dismissToast: dismissPersistentToast,
  }
}
